// © 2026 AttainIP Engineering

/**
 * Turn a freshly-minted token pair into THE session (RFC-0510 W39).
 *
 * The step every path that mints tokens has to take: fetch the profile with the
 * NEW access token and write it, with the tokens, into the auth store. What
 * makes it worth a module of its own is the acting-in control: switching
 * organisation mints a new session, and the profile that comes back names a
 * DIFFERENT organisation — so `org_name`/`org_ext_id`/`features` must all be
 * re-read rather than carried over from the session being replaced.
 *
 * ⚠️ `/v1/auth/me` is fetched with the new token explicitly, never with
 * whatever the store happens to hold: during a switch the store still holds the
 * previous organisation's token for the moment this runs.
 */
import { useAuthStore } from '../store/auth';
import { getCurrentUser, type MeResponse, type TokenResponse } from '../api/identity';
import { identifyUser } from '../lib/mixpanel';

/** The token shape every mint returns — login, refresh, exchange, switch. */
export interface MintedTokens {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
}

/**
 * Establish the session these tokens grant, and return the profile behind it.
 *
 * Throws whatever `getCurrentUser` throws (an `ApiRequestError` for a server
 * refusal); the store is left untouched in that case, so a failed adoption
 * cannot leave a half-swapped session behind.
 */
export async function adoptSession(tokens: MintedTokens | TokenResponse): Promise<MeResponse> {
  const user = await getCurrentUser(tokens.access_token);

  useAuthStore.getState().setAuth({
    user: {
      id: user.id,
      email: user.email,
      display_name: user.display_name,
      organization_id: user.organization_id,
      roles: user.roles,
      audiences: user.audiences,
      // RFC-0510 W06 — acting-org identity for the active-organisation
      // indicator, carried on the session so it paints synchronously.
      org_name: user.org_name,
      org_ext_id: user.org_ext_id,
      // RFC-0500 A5 — carried on the session so route guards stay sync.
      features: user.features,
    },
    accessToken: tokens.access_token,
    expiresIn: tokens.expires_in,
    refreshToken: tokens.refresh_token,
  });

  identifyUser(user);
  return user;
}
